import { useState, useEffect, useRef } from 'react';
import { useRateExperience } from '../../hooks/api/useExperiences';
import { Experience, Content } from '../../hooks/api/useExperiences';
import { StarButtonBase } from './star-button-base';

interface ExperienceStarButtonProps {
  experience: Experience | Content;
  className?: string;
}

export const ExperienceStarButton: React.FC<ExperienceStarButtonProps> = ({ experience, className = '' }) => {
  const rateExperienceMutation = useRateExperience();
  const [optimisticRating, setOptimisticRating] = useState<number | null>(null);
  const experienceIdRef = useRef(experience.id);

  // Reset optimistic state if experience ID changes
  useEffect(() => {
    if (experienceIdRef.current !== experience.id) {
      experienceIdRef.current = experience.id;
      setOptimisticRating(null);
    }
  }, [experience.id]);

  // Use optimistic rating if available, otherwise use experience rating
  const currentRating = optimisticRating !== null ? optimisticRating : experience.rating || 0;
  const isFavorite = currentRating > 0;
  const isLoading = rateExperienceMutation.isLoading;

  const handleToggle = () => {
    const newRating = isFavorite ? 0 : 1;
    setOptimisticRating(newRating);

    rateExperienceMutation.mutate(
      {
        audioId: experience.id,
        listenerId: experience.listener_role_id,
        rating: newRating,
      },
      {
        onSuccess: () => {
          if (experience.rating === newRating) {
            setOptimisticRating(null);
          }
        },
        onError: () => {
          setOptimisticRating(null);
        },
      },
    );
  };

  useEffect(() => {
    if (optimisticRating !== null && experience.rating === optimisticRating) {
      setOptimisticRating(null);
    }
  }, [experience.rating, optimisticRating]);

  return <StarButtonBase isFavorite={isFavorite} onToggle={handleToggle} isLoading={isLoading} className={className} />;
};
